import { useEffect, useState, type CSSProperties } from 'react';
import { useSettings } from '../settings/SettingsContext';
import { motionAllowed } from '../designs/scene/programs';

export type ChartColors = {
  primary: string;
  ink: string;
  inkSoft: string;
  border: string;
  surface: string;
  success: string;
  font: string;
};

function read(): ChartColors {
  const root = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => root.getPropertyValue(name).trim() || fallback;
  return {
    primary: v('--primary', '#1d3a6b'),
    ink: v('--ink', '#1a1a1a'),
    inkSoft: v('--ink-soft', '#555'),
    border: v('--border', '#ccc'),
    surface: v('--surface', '#fff'),
    success: v('--success', '#2e7d32'),
    font: getComputedStyle(document.body).fontFamily,
  };
}

export function useChartTheme() {
  const { settings } = useSettings();
  const [colors, setColors] = useState<ChartColors>(read);
  useEffect(() => {
    const update = () => setColors(read());
    update();
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-design', 'data-palette', 'data-screen', 'style', 'class'] });
    return () => mo.disconnect();
  }, []);
  const animate = motionAllowed(settings.reduceMotion);
  const axis = { fill: colors.inkSoft, fontSize: 12, fontFamily: colors.font };
  const tooltip: { contentStyle: CSSProperties; labelStyle: CSSProperties; itemStyle: CSSProperties; cursor: { fill: string; stroke: string } } = {
    contentStyle: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, fontFamily: colors.font, fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' },
    labelStyle: { color: colors.ink, fontWeight: 700, marginBottom: 4 },
    itemStyle: { color: colors.ink, padding: 0 },
    cursor: { fill: colors.border, stroke: colors.border },
  };
  return { colors, animate, axis, tooltip, duration: animate ? 700 : 0 };
}

export function headroom(max: number): number {
  const v = Math.max(1, max) * 1.2;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * p >= v) return Math.max(1, Math.ceil(m * p));
  return Math.ceil(10 * p);
}

export function countTicks(top: number, max: number): number[] {
  if (max <= 4) return Array.from({ length: Math.floor(top) + 1 }, (_, i) => i);
  const step = Math.max(1, Math.ceil(max / 4));
  const out: number[] = [];
  for (let v = 0; v <= top; v += step) out.push(v);
  return out;
}
